import { useEffect, useRef, useState } from "react";
import "@/App.css";
import {
    Monitor,
    GraduationCap,
    Leaf,
    PawPrint,
    Sofa,
    Package,
    Box,
    Globe,
    Phone,
    Mail,
    MapPin,
    ArrowUpRight,
    Check,
    Calendar,
    Star,
} from "lucide-react";

const PHONE_DISPLAY = "07 64 47 31 17";
const PHONE_HREF = "tel:+33764473117";
const EMAIL = "fetissovb@gmail.com";
const EMAIL_HREF = `mailto:${EMAIL}`;
const BOOKING_URL =
    "https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ17Lj5seUp9uoYxJCbVo2ZBRdWYZ3MPY_yFhTkfmUuOguU7TwwVm0QEAGYVwLewSjjxYk-kWAFd";
const REVIEW_URL = "https://g.page/r/CQT08xXG9Gk3EBM/review";

const SERVICES = [
    {
        icon: Monitor,
        title: "Réparation PC",
        description: "Assemblage, installation Windows, dépannage.",
        details:
            "À domicile ou chez moi. Diagnostic offert, nettoyage, réinstallation propre.",
    },
    {
        icon: GraduationCap,
        title: "Cours informatique",
        description: "Pour seniors, à domicile, à votre rythme.",
        details:
            "Email, photos, visio avec la famille, sécurité de base. Avec patience et sans jargon.",
    },
    {
        icon: Leaf,
        title: "Tonte de pelouse",
        description: "Jardin entretenu, ramassage de feuilles.",
        details:
            "Petites et moyennes surfaces. Je viens avec mon matériel si besoin.",
    },
    {
        icon: PawPrint,
        title: "Garde d'animaux",
        description: "Garde pendant vos vacances.",
        details:
            "Visites quotidiennes, promenades, nourriture, câlins inclus.",
    },
    {
        icon: Sofa,
        title: "Montage de meubles",
        description: "IKEA et autres.",
        details:
            "Notice illisible ? Vis en trop ? Je m'occupe du montage de A à Z.",
    },
    {
        icon: Package,
        title: "Aide au déménagement",
        description: "Portage, emballage, chargement.",
        details:
            "Coup de main pour vider un appartement, charger un utilitaire, ou juste les cartons lourds.",
    },
    {
        icon: Box,
        title: "Impression 3D",
        description: "Pièces, décorations & cadeaux sur mesure.",
        details:
            "Pièce cassée à reproduire, déco personnalisée, cadeau original — envoyez-moi votre idée.",
    },
    {
        icon: Globe,
        title: "Création de sites web",
        description: "Site vitrine pour votre activité — devis gratuit.",
        details:
            "Un site simple, propre et mobile, prêt à être lié à votre fiche Google. Devis offert.",
    },
];

const Reveal = ({ children, delay = 0, className = "" }) => {
    const ref = useRef(null);
    const [shown, setShown] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const obs = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setShown(true);
                    obs.disconnect();
                }
            },
            { threshold: 0.12 },
        );
        obs.observe(el);
        return () => obs.disconnect();
    }, []);

    return (
        <div
            ref={ref}
            style={{ transitionDelay: `${delay}ms` }}
            className={`reveal ${shown ? "in" : ""} ${className}`}
        >
            {children}
        </div>
    );
};

const Header = () => (
    <header
        data-testid="site-header"
        className="sticky top-0 z-50 bg-bg-primary/85 backdrop-blur-xl border-b border-line-subtle"
    >
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 h-16 flex items-center justify-between">
            <a
                href="#top"
                data-testid="logo-link"
                className="font-display text-2xl tracking-tight text-ink-primary"
            >
                Maxim<span className="text-brand">.</span>
            </a>
            <nav className="hidden md:flex items-center gap-10 text-sm font-medium text-ink-secondary">
                <a
                    href="#services"
                    data-testid="nav-services"
                    className="hover:text-ink-primary transition-colors"
                >
                    Services
                </a>
                <a
                    href="#about"
                    data-testid="nav-about"
                    className="hover:text-ink-primary transition-colors"
                >
                    À propos
                </a>
                <a
                    href="#contact"
                    data-testid="nav-contact"
                    className="hover:text-ink-primary transition-colors"
                >
                    Contact
                </a>
            </nav>
            <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="header-booking-btn"
                className="inline-flex items-center gap-2 bg-brand text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-brand-hover transition-colors shadow-sm"
            >
                <Calendar className="w-4 h-4" strokeWidth={2} />
                <span className="hidden sm:inline">Prendre rendez-vous</span>
                <span className="sm:hidden">RDV</span>
            </a>
        </div>
    </header>
);

const Hero = () => (
    <section
        id="top"
        data-testid="hero-section"
        className="relative overflow-hidden"
    >
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 pt-20 pb-24 sm:pt-28 sm:pb-32 lg:pt-32 lg:pb-40 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-7 stagger">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-bg-secondary border border-line-subtle text-xs tracking-[0.18em] uppercase text-ink-secondary font-semibold">
                    <MapPin className="w-3.5 h-3.5" strokeWidth={2} />
                    Montigny · Seine-Maritime
                </div>

                <h1
                    data-testid="hero-headline"
                    className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-ink-primary mt-6"
                >
                    Votre jeune voisin,{" "}
                    <em className="italic font-normal text-brand">
                        disponible pour vous aider.
                    </em>
                </h1>

                <p className="mt-6 max-w-xl text-lg sm:text-xl leading-relaxed text-ink-secondary">
                    Je m'appelle Maxim. De la réparation de PC au montage de
                    meubles, en passant par la garde d'animaux ou la tonte de
                    pelouse — j'apporte un coup de main fiable et soigné, près
                    de chez vous.
                </p>

                <div className="mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4">
                    <a
                        href={BOOKING_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="hero-booking-btn"
                        className="group inline-flex items-center justify-center gap-3 bg-brand text-white px-7 py-4 rounded-full font-medium hover:bg-brand-hover transition-all shadow-sm hover:shadow-md"
                    >
                        <Calendar className="w-5 h-5" strokeWidth={2} />
                        <span>Prendre rendez-vous</span>
                    </a>
                    <a
                        href={PHONE_HREF}
                        data-testid="hero-call-btn"
                        className="group inline-flex items-center justify-center gap-3 bg-transparent border border-ink-primary text-ink-primary px-7 py-4 rounded-full font-medium hover:bg-ink-primary hover:text-bg-primary transition-colors"
                    >
                        <Phone className="w-5 h-5" strokeWidth={2} />
                        <span>{PHONE_DISPLAY}</span>
                    </a>
                </div>

                <ul className="mt-10 flex flex-wrap gap-x-7 gap-y-3 text-sm text-ink-secondary">
                    <li className="inline-flex items-center gap-2">
                        <Check
                            className="w-4 h-4 text-brand"
                            strokeWidth={2.5}
                        />
                        Devis gratuit
                    </li>
                    <li className="inline-flex items-center gap-2">
                        <Calendar
                            className="w-4 h-4 text-brand"
                            strokeWidth={2.5}
                        />
                        Disponible toute la semaine
                    </li>
                    <li className="inline-flex items-center gap-2">
                        <MapPin
                            className="w-4 h-4 text-brand"
                            strokeWidth={2.5}
                        />
                        Montigny et alentours
                    </li>
                </ul>
            </div>

            <div className="lg:col-span-5">
                <div className="relative aspect-[4/5] w-full max-w-md mx-auto lg:ml-auto">
                    <div className="absolute -inset-3 sm:-inset-4 bg-bg-secondary rounded-[2rem] -rotate-2" />
                    <div className="absolute inset-0 rounded-[1.75rem] overflow-hidden border border-line-subtle shadow-md">
                        <img
                            src="https://images.unsplash.com/photo-1649768870222-17848797d6b4?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                            alt="Jeune homme professionnel et souriant"
                            className="w-full h-full object-cover"
                            data-testid="hero-portrait"
                        />
                    </div>
                    <a
                        href={REVIEW_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="hero-review-badge"
                        className="absolute -bottom-5 -left-5 bg-bg-tertiary border border-line-subtle rounded-2xl px-5 py-3 shadow-sm hover:shadow-md transition-shadow flex items-center gap-3"
                    >
                        <div className="flex">
                            {[0, 1, 2, 3, 4].map((i) => (
                                <Star
                                    key={i}
                                    className="w-4 h-4 fill-amber-400 text-amber-400"
                                    strokeWidth={1}
                                />
                            ))}
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.22em] text-ink-secondary font-semibold leading-none">
                                Google
                            </div>
                            <div className="text-sm text-ink-primary font-medium mt-1 leading-none">
                                Laisser un avis
                            </div>
                        </div>
                    </a>
                </div>
            </div>
        </div>
    </section>
);

const ServiceCard = ({ icon: Icon, title, description, details, index }) => (
    <Reveal delay={index * 60}>
        <article
            data-testid={`service-card-${index}`}
            className="group h-full bg-bg-tertiary border border-line-subtle rounded-2xl p-7 sm:p-8 hover:shadow-md hover:-translate-y-1 transition-all duration-300 flex flex-col"
        >
            <div className="w-12 h-12 rounded-xl bg-bg-secondary flex items-center justify-center mb-6 group-hover:bg-brand group-hover:text-white transition-colors">
                <Icon className="w-6 h-6" strokeWidth={1.5} />
            </div>
            <h3 className="font-display text-2xl text-ink-primary tracking-tight">
                {title}
            </h3>
            <p className="mt-2 text-base text-ink-secondary font-medium">
                {description}
            </p>
            <p className="mt-4 text-sm leading-relaxed text-ink-secondary/90">
                {details}
            </p>
        </article>
    </Reveal>
);

const Services = () => (
    <section
        id="services"
        data-testid="services-section"
        className="bg-bg-primary py-24 sm:py-32"
    >
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
            <Reveal>
                <div className="max-w-2xl">
                    <div className="text-xs uppercase tracking-[0.22em] text-ink-secondary font-bold">
                        Ce que je propose
                    </div>
                    <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-ink-primary mt-4">
                        Une aide polyvalente,{" "}
                        <em className="italic font-normal text-brand">
                            pour votre quotidien.
                        </em>
                    </h2>
                    <p className="mt-5 text-lg text-ink-secondary leading-relaxed">
                        Huit services concrets. Un seul interlocuteur, à
                        l'écoute, soigneux, et toujours à un coup de fil de chez
                        vous.
                    </p>
                </div>
            </Reveal>

            <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-7">
                {SERVICES.map((s, i) => (
                    <ServiceCard key={s.title} index={i} {...s} />
                ))}
            </div>
        </div>
    </section>
);

const About = () => (
    <section
        id="about"
        data-testid="about-section"
        className="bg-bg-secondary py-24 sm:py-32 relative overflow-hidden grain"
    >
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 relative">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
                <div className="lg:col-span-5">
                    <div className="text-xs uppercase tracking-[0.22em] text-ink-secondary font-bold">
                        À propos
                    </div>
                    <h2 className="font-display text-4xl sm:text-5xl tracking-tight text-ink-primary mt-4">
                        Un voisin de confiance,{" "}
                        <em className="italic font-normal text-brand">
                            pas une grosse boîte.
                        </em>
                    </h2>
                </div>
                <div className="lg:col-span-7 space-y-6 text-lg leading-relaxed text-ink-secondary">
                    <p>
                        Je suis Maxim, basé à Montigny en Seine-Maritime. J'aime
                        rendre service, j'ai les mains adroites, et je préfère
                        prendre le temps de bien faire plutôt que de bâcler.
                    </p>
                    <p>
                        Que ce soit pour réinstaller un PC qui rame, montrer à
                        un grand-parent comment envoyer une photo, ou monter une
                        bibliothèque IKEA un dimanche après-midi — je viens, je
                        m'applique, et je repars quand c'est propre.
                    </p>
                    <div className="pt-2 grid grid-cols-2 gap-6 text-sm">
                        <div>
                            <div className="text-xs uppercase tracking-[0.22em] text-ink-secondary font-bold">
                                Zone
                            </div>
                            <div className="mt-2 font-display text-2xl text-ink-primary">
                                Montigny (76)
                            </div>
                        </div>
                        <div>
                            <div className="text-xs uppercase tracking-[0.22em] text-ink-secondary font-bold">
                                Disponibilité
                            </div>
                            <div className="mt-2 font-display text-2xl text-ink-primary">
                                Toute la semaine
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
);

const Contact = () => (
    <section
        id="contact"
        data-testid="contact-section"
        className="bg-bg-primary py-24 sm:py-32"
    >
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
            <Reveal>
                <div className="max-w-3xl">
                    <div className="text-xs uppercase tracking-[0.22em] text-ink-secondary font-bold">
                        Contact
                    </div>
                    <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-ink-primary mt-4">
                        Un besoin, une question ?{" "}
                        <em className="italic font-normal text-brand">
                            Appelez-moi.
                        </em>
                    </h2>
                    <p className="mt-5 text-lg text-ink-secondary leading-relaxed">
                        Le plus simple, c'est encore un coup de téléphone. Sinon,
                        un mail fonctionne très bien — je réponds rapidement.
                    </p>
                </div>
            </Reveal>

            <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6">
                <a
                    href={BOOKING_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-booking-card"
                    className="group bg-ink-primary text-bg-primary rounded-3xl p-8 sm:p-10 flex flex-col justify-between min-h-[240px] hover:bg-brand transition-colors md:col-span-3 lg:col-span-1"
                >
                    <div className="flex items-start justify-between">
                        <Calendar className="w-7 h-7" strokeWidth={1.5} />
                        <ArrowUpRight
                            className="w-6 h-6 opacity-60 group-hover:opacity-100 group-hover:translate-x-1 group-hover:-translate-y-1 transition-all"
                            strokeWidth={1.5}
                        />
                    </div>
                    <div className="mt-10">
                        <div className="text-xs uppercase tracking-[0.22em] opacity-70 font-bold">
                            Réserver un créneau
                        </div>
                        <div className="font-display text-3xl mt-2 tracking-tight leading-tight">
                            Prendre rendez-vous en ligne
                        </div>
                        <div className="text-sm opacity-80 mt-3">
                            Choisissez l'horaire qui vous arrange.
                        </div>
                    </div>
                </a>

                <a
                    href={PHONE_HREF}
                    data-testid="contact-call-card"
                    className="group bg-bg-tertiary border border-line-subtle text-ink-primary rounded-3xl p-8 sm:p-10 flex flex-col justify-between min-h-[240px] hover:border-ink-primary transition-colors"
                >
                    <div className="flex items-start justify-between">
                        <Phone className="w-7 h-7" strokeWidth={1.5} />
                        <ArrowUpRight
                            className="w-6 h-6 opacity-50 group-hover:opacity-100 group-hover:translate-x-1 group-hover:-translate-y-1 transition-all"
                            strokeWidth={1.5}
                        />
                    </div>
                    <div className="mt-10">
                        <div className="text-xs uppercase tracking-[0.22em] text-ink-secondary font-bold">
                            Téléphone
                        </div>
                        <div className="font-display text-3xl sm:text-4xl mt-2 tracking-tight">
                            {PHONE_DISPLAY}
                        </div>
                    </div>
                </a>

                <a
                    href={EMAIL_HREF}
                    data-testid="contact-email-card"
                    className="group bg-bg-tertiary border border-line-subtle text-ink-primary rounded-3xl p-8 sm:p-10 flex flex-col justify-between min-h-[240px] hover:border-ink-primary transition-colors"
                >
                    <div className="flex items-start justify-between">
                        <Mail className="w-7 h-7" strokeWidth={1.5} />
                        <ArrowUpRight
                            className="w-6 h-6 opacity-50 group-hover:opacity-100 group-hover:translate-x-1 group-hover:-translate-y-1 transition-all"
                            strokeWidth={1.5}
                        />
                    </div>
                    <div className="mt-10">
                        <div className="text-xs uppercase tracking-[0.22em] text-ink-secondary font-bold">
                            Email
                        </div>
                        <div className="font-display text-2xl sm:text-3xl mt-2 tracking-tight break-all">
                            {EMAIL}
                        </div>
                    </div>
                </a>
            </div>

            <div className="mt-10 flex flex-wrap items-center justify-between gap-4 text-sm text-ink-secondary">
                <a
                    href={BOOKING_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-availability-link"
                    className="inline-flex items-center gap-2 text-ink-secondary hover:text-ink-primary transition-colors"
                >
                    <Calendar className="w-4 h-4 text-brand" strokeWidth={2} />
                    Voir mes disponibilités · Devis gratuit
                    <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2} />
                </a>
                <a
                    href={REVIEW_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-review-link"
                    className="inline-flex items-center gap-2 text-ink-primary hover:text-brand transition-colors font-medium"
                >
                    <Star
                        className="w-4 h-4 fill-amber-400 text-amber-400"
                        strokeWidth={1}
                    />
                    Vous avez aimé ? Laissez un avis Google
                    <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
                </a>
            </div>
        </div>
    </section>
);

const Footer = () => (
    <footer
        data-testid="site-footer"
        className="bg-bg-secondary border-t border-line-subtle"
    >
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div className="flex items-center gap-6">
                <div className="font-display text-xl text-ink-primary">
                    Maxim<span className="text-brand">.</span>
                </div>
                <a
                    href={REVIEW_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="footer-review-link"
                    className="inline-flex items-center gap-1.5 text-sm text-ink-secondary hover:text-ink-primary transition-colors"
                >
                    <Star
                        className="w-3.5 h-3.5 fill-amber-400 text-amber-400"
                        strokeWidth={1}
                    />
                    Avis Google
                </a>
            </div>
            <div className="text-xs uppercase tracking-[0.22em] text-ink-secondary font-semibold flex flex-wrap gap-x-6 gap-y-2">
                <span>Montigny · Seine-Maritime (76380)</span>
                <span>© {new Date().getFullYear()} — Services à la personne</span>
            </div>
        </div>
    </footer>
);

function App() {
    return (
        <div className="App font-body text-ink-primary bg-bg-primary min-h-screen">
            <Header />
            <main>
                <Hero />
                <Services />
                <About />
                <Contact />
            </main>
            <Footer />
        </div>
    );
}

export default App;
